import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export type NavDestination = {
  to: "/docs" | "/brand" | "/brand/templates" | "/team" | "/billing" | "/admin";
  label: string;
  icon: ReactNode;
  testId?: string;
};

const ICON = "h-4 w-4 shrink-0";

/**
 * The app shell's primary navigation.
 *
 * Inline as a segmented control from `lg` up, and a single menu button below
 * it. That threshold isn't decoration: in b2c the header also carries the
 * logo, the team switcher, a usage chip, the bell and the account control, and
 * five inline destinations don't fit beside them until ~1024px. Below that the
 * header used to keep rendering everything anyway — flex children shrink below
 * their content by default, so the logo, the switcher and the nav were painted
 * ON TOP of each other from 834px all the way down to 320px.
 *
 * `tests/app-header-fit.b2c.spec.ts` is the guard: it walks a sweep of widths
 * and fails on any two header controls whose boxes intersect. Assert geometry,
 * never visibility — `toBeVisible` passes happily on a control that is
 * completely covered by another one.
 */
export function PrimaryNav({
  items,
  foldBelow = "lg",
}: {
  items: NavDestination[];
  /**
   * Where the inline nav gives up. `lg` for b2c, whose header also carries a
   * team switcher, a usage chip and five destinations. Enterprise carries a
   * logo, one or two destinations and an email, so it stays inline from `sm`
   * — folding it that early would hide a two-item nav behind a button on a
   * laptop. Literal class strings: Tailwind can't see interpolated ones.
   */
  foldBelow?: "lg" | "sm";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const inlineAt = foldBelow === "lg" ? "hidden lg:flex" : "hidden sm:flex";
  const foldedAt = foldBelow === "lg" ? "relative lg:hidden" : "relative sm:hidden";

  // Dismiss on outside click / Escape — same posture as the team switcher.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      {/* Inline — the roomy layout. */}
      <nav
        aria-label="Primary"
        className={`${inlineAt} items-center gap-0.5 rounded-xl bg-white/[0.03] border mx-divider p-1`}
      >
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            data-testid={it.testId}
            aria-label={it.label}
            className="group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors duration-150"
            inactiveProps={{
              className: "text-white/55 hover:text-white hover:bg-white/[0.06]",
            }}
            activeProps={{
              className:
                "bg-white/[0.10] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)]",
            }}
          >
            {it.icon}
            <span>{it.label}</span>
          </Link>
        ))}
      </nav>

      {/* Folded — one button, every destination behind it. */}
      <div ref={rootRef} className={foldedAt}>
        <button
          type="button"
          data-testid="nav-menu-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={open ? "Close menu" : "Menu"}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-center h-9 w-9 rounded-lg border mx-divider bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors duration-150"
        >
          {open ? (
            <X className={ICON} aria-hidden />
          ) : (
            <Menu className={ICON} aria-hidden />
          )}
        </button>
        {open && (
          <nav
            id={menuId}
            aria-label="Primary"
            data-testid="nav-menu"
            className="absolute right-0 top-full mt-2 z-50 min-w-[12rem] rounded-md border mx-divider bg-[#141517] shadow-xl shadow-black/50 py-1.5"
          >
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testId}
                aria-label={it.label}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-sm transition-colors duration-150"
                inactiveProps={{
                  className: "text-white/70 hover:text-white hover:bg-white/[0.06]",
                }}
                activeProps={{
                  className: "text-white bg-white/[0.08]",
                }}
              >
                {it.icon}
                <span>{it.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </div>
    </>
  );
}
